<template>
  <section class="other-details">
    <template v-if="enforceSpecialRequests">
      <div class="type-of-unit">
        <pu-popover>
          <template #content>
            <div>
              <translate :tt="{ campsiteName: campsiteName }">
                To assign your pitch, {campsiteName} would like to know the type
                of accommodation you are bringing and how big it is.
              </translate>
            </div>
          </template>
        </pu-popover>

        <base-select
          :select-options="unitTypes"
          :required="true"
          :validation-rules="unitTypesValidationSchema"
          name="type_of_unit"
          @change="unitType = $event.selectedValue"
        >
          <template #label>
            <translate>Type of unit</translate>
          </template>
          <template #helpText>
            <booking-disallowed-subcategories
              class="booking-disallowed-subcategories"
              :disallowed-subcategories="details.disallowedSubcategories"
            />
          </template>
          <template #errorMessage>
            <translate>
              Please select the type of unit you are planning to bring
            </translate>
          </template>
        </base-select>
      </div>

      <div class="size-of-unit">
        <translate tag="label"> Approximate size of unit </translate>

        <div class="units">
          <base-input
            v-model="unitWidth"
            v-format-number="1"
            :placeholder="$t('metres')"
            :required="true"
            :validation-rules="unitWidthValidationSchema"
            autocomplete="off"
            class="unit"
            inputmode="decimal"
            name="dimensions_width"
            type="text"
          >
            <template #label>
              <translate>Width (metres)</translate>
            </template>
            <template #errorMessage="{ failedRules }">
              <translate v-if="failedRules.includes('required')">
                Please tell us the width of your unit
              </translate>
              <translate
                v-else-if="failedRules.includes('max_value')"
                :tt="{ maxWidth: maxWidth }"
              >
                The maximum allowed width is { maxWidth }m
              </translate>
              <translate v-else-if="failedRules.includes('min_value')">
                Please enter a width
              </translate>
              <translate v-else-if="failedRules.includes('invalid_type')">
                Please enter a number
              </translate>
              <span v-else>{{ failedRules }}</span>
            </template>
          </base-input>
          <span class="multiply">×</span>
          <base-input
            v-model="unitDepth"
            v-format-number="1"
            :placeholder="$t('metres')"
            autocomplete="off"
            name="dimensions_depth"
            :required="true"
            type="text"
            :validation-rules="unitDepthValidationSchema"
            class="unit"
            inputmode="decimal"
          >
            <template #label>
              <translate>Length (metres)</translate>
            </template>
            <template #errorMessage="{ failedRules }">
              <translate v-if="failedRules.includes('required')">
                Please tell us the length of your unit
              </translate>
              <translate
                v-else-if="failedRules.includes('max_value')"
                :tt="{ maxDepth: maxDepth }"
              >
                The maximum allowed length is { maxDepth }m
              </translate>
              <translate v-else-if="failedRules.includes('min_value')">
                Please enter a length
              </translate>
              <translate v-else-if="failedRules.includes('invalid_type')">
                Please enter a number
              </translate>
              <span v-else>{{ failedRules }}</span>
            </template>
          </base-input>
        </div>

        <div class="help-text" v-if="details.dimensionsDetailed">
          <pitch-type-dimensions :pitch-type="details" />
        </div>
      </div>
    </template>

    <div v-if="requireCarRegistration" class="other-details-subsection">
      <base-input
        v-model="carRegistration"
        :required="requireCarRegistration"
        :placeholder="$t('Vehicle registration number number placeholder')"
        autocapitalize="characters"
        autocorrect="off"
        maxlength="55"
        name="car_registration_number"
        :validation-rules="carRegistrationValidationSchema"
        class="vehicle-registration-input"
      >
        <template #label>
          {{ $t('Vehicle registration number') }}
        </template>
        <template #helpText>
          {{
            $t('Vehicle registration number helptext {campsiteName}', {
              campsiteName,
            })
          }}
        </template>
        <template #errorMessage>
          {{
            $t(
              'Please tell us your registration number. Type None if you are not bringing a vehicle, or do not know the registration yet.',
            )
          }}
        </template>
      </base-input>
    </div>

    <template v-if="requirePartyNames">
      <base-textarea
        v-model="partyNames"
        :required="requirePartyNames"
        :rows="personsCount"
        :autogrow="false"
        name="names_of_all_party_members"
        :validation-rules="partyNamesValidationSchema"
        :data-sctrack="`${String($route.name)}--BookingOtherDetails-party-names`"
        css-class="asblock"
        autocorrect="off"
      >
        <template #label>
          {{
            $t('Names of the {people} in your party', {
              people: personsCount,
            })
          }}
        </template>
        <template #helpText>
          {{
            $t(
              '{campsiteName} requests each name so they can register your party',
              { campsiteName },
            )
          }}
        </template>
        <template #errorMessage>
          {{
            $t(
              'Please list the members of your party so that we can inform {campsiteName}',
              { campsiteName },
            )
          }}
        </template>
      </base-textarea>
    </template>

    <div v-if="requestGroupName" class="other-details-subsection">
      <base-input
        v-model="groupName"
        :required="false"
        name="group_name"
        css-class="asblock"
        :data-sctrack="`${String($route.name)}--BookingOtherDetails-group-name`"
      >
        <template #label>
          {{ $t('Group name') }}
        </template>
        <template #helpText>
          {{
            $t('Let {campsiteName} know you are booking as part of a group', {
              campsiteName,
            })
          }}.
        </template>
      </base-input>
    </div>

    <base-textarea
      v-model="specialRequests"
      name="special_requests"
      rows="2"
      css-class="asblock"
      :data-sctrack="`${String($route.name)}--BookingOtherDetails-special-requests`"
    >
      <template #label>
        <translate>Special requests</translate>
      </template>
      <template #helpText>
        <translate tag="span" :tt="{ campsiteName }">
          Booking other details special requests helptext {campsiteName}
        </translate>
      </template>
    </base-textarea>
  </section>
</template>

<script lang="ts">
import * as z from 'zod'
import { getLocaleInfo } from '~/lang/locales'
import { NumberParser } from '~/utility/i18n/number-parser'
import {
  Component,
  Prop,
  Vue,
  Watch,
  toNative,
} from '~/utility/pu-class-decorator'
import { BookingInfo } from '../data/schema'
import { OtherDetails } from '../submit/schema'

@Component({
  emits: ['update:modelValue'],
})
class BookingOtherDetails extends Vue {
  @Prop()
  otherDetails: OtherDetails

  @Prop()
  details: BookingInfo

  @Prop()
  adults: number

  @Prop()
  childAges: number[]

  @Prop({ default: false })
  enforceSpecialRequests: boolean

  @Prop({ default: false })
  requireCarRegistration: boolean

  @Prop({ default: false })
  requirePartyNames: boolean

  @Prop({ default: false })
  requestGroupName: boolean

  @Prop()
  campsiteName: string

  @Prop()
  isByo: boolean

  @Prop()
  dimensions: string

  @Prop()
  dimensionsExample: string

  defaultDimensionMax = 10

  unitTypesValidationSchema = z
    .string({ required_error: 'required' })
    .min(1, { message: 'required' })

  get unitWidthValidationSchema() {
    return z.preprocess(
      (val) => this.parseNumberOrEmpty(val as string),
      z
        .number({
          required_error: 'required',
          invalid_type_error: 'invalid_type',
        })
        .max(this.maxWidth, { message: 'max_value' })
        .min(0.1, { message: 'min_value' }),
    )
  }

  get unitDepthValidationSchema() {
    return z.preprocess(
      (val) => this.parseNumberOrEmpty(val as string),
      z
        .number({
          required_error: 'required',
          invalid_type_error: 'invalid_type',
        })
        .max(this.maxDepth, { message: 'max_value' })
        .min(0.1, { message: 'min_value' }),
    )
  }

  carRegistrationValidationSchema: any = undefined
  @Watch('requireCarRegistration', { immediate: true })
  requireCarRegistrationWatcher() {
    if (this.requireCarRegistration) {
      this.carRegistrationValidationSchema = z
        .string({ required_error: 'required' })
        .min(1, { message: 'required' })
    } else {
      this.carRegistrationValidationSchema = z.string().nullable()
    }
  }

  partyNamesValidationSchema: any = undefined
  @Watch('requirePartyNames', { immediate: true })
  requirePartyNamesWatcher() {
    if (this.requirePartyNames) {
      this.partyNamesValidationSchema = z
        .string({ required_error: 'required' })
        .min(1, { message: 'required' })
    } else {
      this.partyNamesValidationSchema = z.string().nullable()
    }
  }

  input(otherDetails: Partial<OtherDetails>): OtherDetails {
    const newOtherDetails = Object.assign({}, this.otherDetails, otherDetails)
    this.$emit('update:modelValue', newOtherDetails)
    return newOtherDetails
  }

  get unitType(): string {
    return this.otherDetails.unitType || ''
  }

  set unitType(unitType: string) {
    this.input({ unitType })
  }

  get unitWidth(): number | string {
    return this.otherDetails.unitWidth || ''
  }

  set unitWidth(unitWidth: string) {
    this.input({ unitWidth })
  }

  get unitDepth(): string {
    return this.otherDetails.unitDepth || ''
  }

  set unitDepth(unitDepth: string) {
    this.input({ unitDepth })
  }

  get carRegistration(): string {
    return this.otherDetails.carRegistration || ''
  }

  set carRegistration(carRegistration: string) {
    this.input({ carRegistration })
  }

  get partyNames(): string {
    return this.otherDetails.partyNames || ''
  }

  set partyNames(partyNames: string) {
    this.input({ partyNames })
  }

  get groupName(): string {
    return this.otherDetails.groupName || ''
  }

  set groupName(groupName: string) {
    this.input({ groupName })
  }

  get specialRequests(): string {
    return this.otherDetails.specialRequests || ''
  }

  set specialRequests(specialRequests: string) {
    this.input({ specialRequests })
  }

  get maximumDimensionInMetres() {
    if (
      this.details &&
      (this.details.dimensionsType === 'max' ||
        this.details.dimensionsType === 'unlimited')
    ) {
      return {
        depth: Number(this.details.dimensionsDepth) || this.defaultDimensionMax,
        width: Number(this.details.dimensionsWidth) || this.defaultDimensionMax,
      }
    }
    return {
      depth: Number.POSITIVE_INFINITY,
      width: Number.POSITIVE_INFINITY,
    }
  }

  get maxWidth() {
    return this.maximumDimensionInMetres.width
  }

  get maxDepth() {
    return this.maximumDimensionInMetres.depth
  }

  get unitTypes() {
    if (!this.details.subcategoriesRequiringDimensions) {
      return []
    }
    return [
      {
        value: '',
        name: '---',
        selected: !this.unitType,
      },
      ...this.details.subcategoriesRequiringDimensions.map((subCat) => {
        return {
          value: subCat.id,
          name: subCat.name,
          selected: this.unitType === subCat.id,
        }
      }),
    ]
  }

  get personsCount(): number {
    return this.adults + this.childAges.length
  }

  parseNumberOrEmpty(value: string): number | string | undefined {
    if (value === '') {
      return undefined
    }
    return this.parseNumber(value)
  }

  // this is used to parse a i18n number into a javascript number
  // just before validation
  parseNumber(value: string): number {
    const localeInfo = getLocaleInfo(this.$route.params.lang)
    const parser = new NumberParser(localeInfo.iso)
    return parser.parse(value)
  }
}

export default toNative(BookingOtherDetails)
</script>

<style scoped>
.other-details > span {
  margin-bottom: 1.5em;
  display: block;
}

.other-details :deep(.select) {
  margin: 0;
  display: block;
}

.other-details :deep(.select select) {
  font-size: 1.24em;
}

.type-of-unit + .size-of-unit {
  margin-top: 1em;
}

.size-of-unit :deep(label) {
  margin: 0 !important;
}

.units {
  align-items: flex-start;
  display: flex;
  flex-direction: row;
  gap: 1em;
  justify-content: space-between;
  margin-top: 0;
}

.multiply {
  margin: 2em auto auto auto;
}

.unit {
  max-width: calc((100% - 2em) / 2);
}

.unit :deep(label) {
  font-weight: normal;
}

.other-details-subsection {
  margin-top: 1em;
}

.booking-disallowed-subcategories :deep(strong) {
  font-weight: normal;
}

.other-details :deep(.pu-popover) {
  display: flex;
  align-items: flex-start;
  justify-content: flex-end;
  margin: -1em 0 -1em;
}

.vehicle-registration-input :deep(input::placeholder) {
  font-size: 0.875em;
}
</style>
